// -----------------------------------------------------------------------------
// src/utils/numberFormatter.js
// Comprehensive number formatting utilities for consistent display across components
// -----------------------------------------------------------------------------

/**
 * Formats a number with commas as thousand separators
 * @param {number|string} number - The number to format
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 0)
 * @param {string} options.locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted number string
 */
export const formatNumber = (number, options = {}) => {
  const { decimals = 0, locale = 'en-US' } = options;
  
  // Handle null, undefined, or empty string
  if (number === null || number === undefined || number === '') {
    return '0';
  }
  
  // Convert to number if it's a string
  const num = typeof number === 'string' ? parseFloat(number) : number;
  
  // Handle NaN
  if (isNaN(num)) {
    return '0';
  }
  
  return num.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats a number as currency with proper symbol and commas
 * @param {number|string} amount - The amount to format
 * @param {Object} options - Formatting options
 * @param {string} options.currency - Currency code (default: 'USD')
 * @param {string} options.locale - Locale for formatting (default: 'en-US')
 * @param {number} options.decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const { currency = 'USD', locale = 'en-US', decimals = 2 } = options;
  
  // Handle null, undefined, or empty string
  if (amount === null || amount === undefined || amount === '') {
    return '$0.00';
  }
  
  // Convert to number if it's a string
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle NaN
  if (isNaN(num)) {
    return '$0.00';
  }
  
  return num.toLocaleString(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats large numbers with abbreviated suffixes (K, M, B, T)
 * @param {number|string} number - The number to format
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 1)
 * @param {string} options.locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted number with suffix
 */
export const formatCompactNumber = (number, options = {}) => {
  const { decimals = 1, locale = 'en-US' } = options;
  
  // Handle null, undefined, or empty string
  if (number === null || number === undefined || number === '') {
    return '0';
  }
  
  // Convert to number if it's a string
  const num = typeof number === 'string' ? parseFloat(number) : number;
  
  // Handle NaN
  if (isNaN(num)) {
    return '0';
  }
  
  // For numbers less than 1000, just format normally
  if (Math.abs(num) < 1000) {
    return formatNumber(num, { decimals: 0 });
  }
  
  return num.toLocaleString(locale, {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats a percentage with proper symbol
 * @param {number|string} number - The number to format as percentage
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 1)
 * @param {string} options.locale - Locale for formatting (default: 'en-US')
 * @param {boolean} options.multiply - Whether to multiply by 100 (default: false, assumes already in percentage form)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (number, options = {}) => {
  const { decimals = 1, locale = 'en-US', multiply = false } = options;
  
  // Handle null, undefined, or empty string
  if (number === null || number === undefined || number === '') {
    return '0%';
  }
  
  // Convert to number if it's a string
  let num = typeof number === 'string' ? parseFloat(number) : number;
  
  // Handle NaN
  if (isNaN(num)) {
    return '0%';
  }
  
  // Multiply by 100 if needed (for decimal percentages like 0.5 -> 50%)
  if (multiply) {
    num = num * 100;
  }
  
  return num.toLocaleString(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Smart number formatter that chooses the best format based on the number size
 * @param {number|string} number - The number to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.currency - Format as currency (default: false)
 * @param {boolean} options.compact - Use compact notation for large numbers (default: true)
 * @param {number} options.decimals - Number of decimal places (default: 0 for regular, 2 for currency)
 * @returns {string} Formatted number string
 */
export const formatSmart = (number, options = {}) => {
  const { currency = false, compact = true, decimals } = options;
  
  // Handle null, undefined, or empty string
  if (number === null || number === undefined || number === '') {
    return currency ? '$0.00' : '0';
  }
  
  // Convert to number if it's a string
  const num = typeof number === 'string' ? parseFloat(number) : number;
  
  // Handle NaN
  if (isNaN(num)) {
    return currency ? '$0.00' : '0';
  }
  
  // Determine default decimals
  const defaultDecimals = currency ? 2 : 0;
  const finalDecimals = decimals !== undefined ? decimals : defaultDecimals;
  
  if (currency) {
    // For currency, use compact notation if number is very large
    if (compact && Math.abs(num) >= 1000000) {
      return formatCompactNumber(num, { decimals: 1 }) + (num >= 0 ? '' : '');
    }
    return formatCurrency(num, { decimals: finalDecimals });
  } else {
    // For regular numbers, use compact notation if enabled and number is large
    if (compact && Math.abs(num) >= 1000000) {
      return formatCompactNumber(num, { decimals: 1 });
    }
    return formatNumber(num, { decimals: finalDecimals });
  }
};

/**
 * Legacy function for backward compatibility with existing formatAmount function
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatAmount = (amount) => {
  return formatCurrency(amount);
};

// Export all functions as default object for easy importing
export default {
  formatNumber,
  formatCurrency,
  formatCompactNumber,
  formatPercentage,
  formatSmart,
  formatAmount, // backward compatibility
};